/**
 * Global Filter clinic picks.
 *
 * Clinic users already reset to one account when they add a clinic from another
 * account. Internals were skipped, so switching accounts left a mixed/empty
 * clinic set and POST /users/practitioners either skipped or expanded to every
 * clinic. Reset on a single added clinic; leave bulk Select-all intact.
 */
export function clinicsAfterAccountPick(
  previousClinicIds: string[],
  nextClinicIds: string[],
  accountOf: (clinicId: string) => string
): string[] {
  const accounts = new Set(nextClinicIds.map(accountOf));
  if (accounts.size <= 1) {
    return nextClinicIds;
  }

  const added = nextClinicIds.filter((id) => !previousClinicIds.includes(id));
  if (added.length !== 1) {
    return nextClinicIds;
  }

  const account = accountOf(added[0]);
  return nextClinicIds.filter((id) => accountOf(id) === account);
}

export function isInternalPortalRole(role: string | null | undefined): boolean {
  return Boolean(role?.includes('internal'));
}

export const SELECT_CLINICS_FOR_PROVIDERS = 'Select clinics to load providers';

type ClinicAccountSource = {
  id: string;
  parent?: { id?: string | null } | null;
};

/** Clinic id → parent account id. Clinics with no parent are left out of the map. */
export function accountIdByClinicId(clinics: readonly ClinicAccountSource[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const clinic of clinics) {
    const parentId = clinic.parent?.id?.trim();
    if (parentId) map.set(clinic.id, parentId);
  }
  return map;
}

/**
 * Clinic ids for the practitioner list. An empty result means do not fetch.
 * One selected clinic always loads. Several clinics load only when they share
 * one parent account id. An internal selection across accounts, including
 * Select all, is skipped. Clinics with no parent do not collapse into one
 * account, so a batch of them is skipped too. Ids the user cannot access are
 * dropped. An empty filter skips for an internal user and falls back to an
 * external user's own clinics. Ids are sorted so the query cache key is stable.
 */
export function practitionerClinicIdsFor(
  selectedClinicIds: string[],
  accessibleClinicIds: string[],
  isInternal: boolean,
  accountByClinicId: ReadonlyMap<string, string>
): string[] {
  const selected =
    selectedClinicIds.length > 0 ? selectedClinicIds : isInternal ? [] : accessibleClinicIds;
  if (selected.length === 0) {
    return [];
  }
  const inScope = clinicsInScope(selected, accessibleClinicIds);
  if (inScope.length === 0) {
    return [];
  }
  if (isInternal && !isSingleAccount(inScope, accountByClinicId)) {
    return [];
  }
  return [...inScope].sort();
}

function clinicsInScope(selected: string[], accessible: string[]): string[] {
  if (accessible.length === 0) return selected;
  const allowed = new Set(accessible);
  return selected.filter((id) => allowed.has(id));
}

/**
 * One clinic is always one account. A larger set must share one parent id.
 * A clinic with no parent id does not join that account.
 */
function isSingleAccount(
  clinicIds: string[],
  accountByClinicId: ReadonlyMap<string, string>
): boolean {
  if (clinicIds.length === 1) return true;
  let account: string | undefined;
  for (const id of clinicIds) {
    const parentId = accountByClinicId.get(id);
    if (!parentId) return false;
    if (account === undefined) account = parentId;
    else if (account !== parentId) return false;
  }
  return clinicIds.length > 0;
}
